import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiClientError } from '@/lib/api-client';
import {
  activateFacility,
  createFacility,
  deactivateFacility,
  listCompanyOptions,
  listFacilities,
  purgeFacility,
  updateFacility,
  type CompanyOptionListParams,
  type FacilityListParams,
} from './api';
import type { CompanyOption, Facility, FacilityCreateInput, FacilityUpdateInput } from './types';

const KEY = 'facilities' as const;
const COMPANIES_KEY = 'company-options' as const;

export const usePurgeFacility = () => {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: (id) => purgeFacility(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useFacilitiesList = (params: FacilityListParams = {}) => {
  return useQuery<Facility[], ApiClientError>({
    queryKey: [KEY, 'list', params],
    queryFn: () => listFacilities(params),
  });
};

export const useCompanyOptions = (params: CompanyOptionListParams = {}) => {
  return useQuery<CompanyOption[], ApiClientError>({
    queryKey: [COMPANIES_KEY, params],
    queryFn: () => listCompanyOptions(params),
  });
};

export const useCreateFacility = () => {
  const qc = useQueryClient();
  return useMutation<Facility, ApiClientError, FacilityCreateInput>({
    mutationFn: (body) => createFacility(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] });
      qc.invalidateQueries({ queryKey: [COMPANIES_KEY] });
    },
  });
};

export const useUpdateFacility = () => {
  const qc = useQueryClient();
  return useMutation<Facility, ApiClientError, { id: string; body: FacilityUpdateInput }>({
    mutationFn: ({ id, body }) => updateFacility(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useDeactivateFacility = () => {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: (id) => deactivateFacility(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useActivateFacility = () => {
  const qc = useQueryClient();
  return useMutation<Facility, ApiClientError, string>({
    mutationFn: (id) => activateFacility(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};
